import axios, { AxiosError, InternalAxiosRequestConfig, type AxiosAdapter } from 'axios'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/hooks/useToast.ts'
import { toastLicenceBlock } from '@/lib/licence/licenceBlockToast.ts'
import i18n from '@/i18n.ts'
import { LOCAL_STORAGE_LANG_KEY } from '@/lib/constants/constants.ts'
import { shouldQueueMutation } from '@/lib/platform/platformDetection'
import { nativeQueueManager } from '@/lib/nativeSync/nativeQueueManager'
// Network status reads go through the platform wrapper instead of
// directly to `@capacitor/network` so the dev harness can override
// them (see `lib/platform/networkStatus.ts`). In production builds the
// wrapper is a thin pass-through; the harness override + its import
// are tree-shaken away.
import { getStatus as getNetworkStatus } from '@/lib/platform/networkStatus'
import { getApiBaseUrl } from '@/config/apiConfig'
import { SLOW_THRESHOLD_MS, slowConnectionDetector } from '@/lib/slowConnectionDetector'
import { measureLinkLatency } from '@/lib/linkLatencyProbe'
import { useServerWorkStore } from '@/store/useServerWorkStore'
import { hasPendingQueueEntriesForType } from '@/lib/helpers/bgSync/pendingQueueState'
import { shouldRedirectUnauthorizedToLogin } from '@/lib/publicRoutePolicy'
import { reportHttpFailure } from '@/lib/monitoring/httpFailureReporting'
import { adoptSignedMediaUrls } from '@/lib/media/adoptSignedMediaUrls'
import { clientIdentityHeaders } from '@/lib/clientIdentity'
import { isWebUpdateRequired } from '@/store/useWebUpdateStore'
// Dev-harness network override. The import is only USED inside an
// `if (__DEV_HARNESS__) { ... }` block below, so Rollup tree-shakes both
// the symbol use AND this import in production builds — the harness
// module + its zustand dependency are not in `dist/`. Asserted by
// `verify-prod-build`. The transport module is side-effect-free at
// module load (it just exports a getter), which is what makes the
// tree-shake clean.
import { getHarnessAxiosOverride } from '@/lib/devtools/harness/domains/offline/networkTransport'
// Dev-harness mock-202 bus. Same DCE story as the network override —
// only USED inside an `if (__DEV_HARNESS__) { ... }` block in
// `handleNativeQueueRequest` below, so the symbols + the import + the
// bus module + its zustand state are all tree-shaken from production.
// The bus records every mock-202 emission and (optionally) wraps the
// response `data` in a Proxy that flags reads of keys outside
// `{status, metadata}` — that's the "mock-202 cache trap" failure
// category from `audit/domains/offline.md`.
import { emitMock202, wrapMock202Data } from '@/lib/devtools/harness/domains/offline/mock202Bus'
import { STAGE_GUIDE_QUERY_ROOT } from '@/help/guide/stageGuideActions.ts'

const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[API]', ...args)

/**
 * Map API URL segments to React Query cache keys that should be invalidated
 * when a mutation (POST/PATCH/DELETE) succeeds on that endpoint.
 * Broad invalidation is intentional — ensures all related views stay fresh.
 * Add new entries here when adding new API resources or query keys.
 */
//
// `stageGuide` is listed on every resource whose direct (non-queued) write can complete or
// reopen a guide step: a stage's start date (PATCH /project_stages/{id}), deleting the last
// location, plan, issue or own comment, removing a participant. Queued POST/PATCH never reach this map (their
// response is a mock 202); `useQueueDrainInvalidator` asks the guide again once they drain.
const MUTATION_INVALIDATION_MAP: Record<string, string[]> = {
  '/issues': [
    'singleIssue',
    'stageIssuesByIssueType',
    'singleReportIssues',
    'getIssuesSummary',
    'getIssueTypeArchiveQuery',
    STAGE_GUIDE_QUERY_ROOT,
  ],
  '/comments': ['singleIssueComments', 'singleIssueEvents', STAGE_GUIDE_QUERY_ROOT],
  '/media_objects': ['singleIssue', 'singleReportIssues', STAGE_GUIDE_QUERY_ROOT],
  '/reports': ['singleReport', 'singleReportIssues', 'getReportsQuery'],
  '/notifications': [
    'getNotificationsSummary',
    'getNotificationsByStageId',
    'getUnreadNotificationsByStageId',
  ],
  '/locations': ['stageLocations', 'getProjectStageLocations', STAGE_GUIDE_QUERY_ROOT],
  '/statuses': ['projectStatusGroups', 'organizationStatusGroups'],
  '/status_groups': ['projectStatusGroups', 'organizationStatusGroups'],
  '/labels': ['projectLabelGroups', 'organizationLabelGroups'],
  '/label_groups': ['projectLabelGroups', 'organizationLabelGroups'],
  '/issue_types': ['projectIssueTypes', 'organizationIssueTypes', 'currStageIssueTypes'],
  '/project_stages': [
    'orgProjectStages',
    'currStageUsersQuery',
    'currStageIssueTypes',
    STAGE_GUIDE_QUERY_ROOT,
  ],
  '/projects': ['project', 'orgProjectStages', 'orgProjects'],
  '/project_participants': [
    'getProjectStageParticipations',
    'currStageUsersQuery',
    STAGE_GUIDE_QUERY_ROOT,
  ],
  '/project_participations': [
    'getProjectStageParticipations',
    'userProjectParticipations',
    STAGE_GUIDE_QUERY_ROOT,
  ],
  // A template's roles ride on GET /project_templates, which the template choices read.
  '/project_roles': ['currStageIssueTypes', 'projectTemplates'],
  '/organization_members': ['getOrgMembers'],
  '/organizations': ['currentOrganization'],
  '/users': ['user', 'getOrgMembers'],
  '/custom_issue_filters': ['currStageIssueTypes'],
}

/**
 * Mutation URLs that should NOT trigger auto-invalidation (async operations, exports, etc.).
 *
 * `/render-jobs` would otherwise match `/reports` above and refetch the report,
 * its issues and the report list on every PDF request, although enqueueing a
 * render changes none of them.
 */
const MUTATION_INVALIDATION_SKIP: string[] = [
  '/export-pdf',
  '/render-jobs',
  '/login',
  '/reset_password',
]

/**
 * URL segments that route through the SW / native sync queue, paired with the
 * `meta.type` they queue under. When a non-queued method (DELETE today) lands
 * on one of these endpoints while pending entries still exist for that type,
 * the per-response invalidation below is suppressed: refetching now would
 * race the in-flight queue and clobber optimistic state painted by the
 * caller. `useQueueDrainInvalidator` runs a single invalidation once the
 * queue empties, which becomes the canonical reconciliation point.
 */
const QUEUED_ENDPOINT_TYPES: Array<readonly [string, string]> = [
  ['/issues', 'issues'],
  ['/comments', 'comments'],
  ['/media_objects', 'media_objects'],
  ['/locations', 'locations'],
]

class NativeQueuedResponse {
  constructor(
    public readonly response: {
      data: { status: string; metadata: unknown }
      status: number
      statusText: string
      headers: Record<string, string>
      config: InternalAxiosRequestConfig
    },
    public readonly config: InternalAxiosRequestConfig,
  ) {}
}

/**
 * Check if an AxiosError is caused by being offline / no network.
 * These errors have no response (request never reached server) and should
 * not show a toast – the user already knows they're offline.
 */
const isOfflineOrTimeoutError = (error: AxiosError): boolean =>
  !navigator.onLine ||
  error.code === 'ERR_NETWORK' ||
  error.code === 'ECONNABORTED' ||
  error.message === 'Network Error'

/**
 * True when a response is a maintenance-mode 503 (carries X-Maintenance-Mode).
 * Read case-safely: axios may expose response headers as an AxiosHeaders
 * instance (case-insensitive `.get`) or a plain lowercased-key object.
 */
const isMaintenance503 = (error: AxiosError): boolean => {
  if (error.response?.status !== 503) return false
  const headers = error.response.headers as unknown as {
    get?: (name: string) => unknown
    [key: string]: unknown
  }
  const raw =
    typeof headers?.get === 'function'
      ? headers.get('X-Maintenance-Mode')
      : headers?.['x-maintenance-mode']
  return String(raw ?? '').toLowerCase() === 'true'
}

const SEVEN_DAYS = 1000 * 60 * 60 * 24 * 7

const api = axios.create({
  baseURL: getApiBaseUrl() || '/',
  withCredentials: true,
  timeout: 15_000,
})

api.defaults.headers.common['Forward-To-Api'] = 'true'

api.defaults.headers.get['Accept'] = 'application/ld+json'

api.defaults.headers.patch['Accept'] = 'application/ld+json'
api.defaults.headers.patch['Content-Type'] = 'application/merge-patch+json'

api.defaults.headers.post['Accept'] = 'application/ld+json'
api.defaults.headers.post['Content-Type'] = 'application/ld+json'

import { isQueueableUrl, QUEUE_BYPASS_PATTERNS } from '@/lib/queueBypass'
// Re-export for callers that previously imported these from api-client.
export { isQueueableUrl, QUEUE_BYPASS_PATTERNS }

const checkNetworkConnected = async (): Promise<boolean> => {
  try {
    const status = await getNetworkStatus()
    return status.connected
  } catch {
    return navigator.onLine
  }
}

// Always queue POST/PATCH requests first, then immediately sync if online
const handleNativeQueueRequest = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const method = config.method?.toUpperCase()
  const url = config.url || ''
  const isQueueable = isQueueableUrl(url)

  if ((method === 'POST' || method === 'PATCH') && isQueueable) {
    debug('iOS native queuing request:', url)

    let formData: FormData | undefined
    if (config.data instanceof FormData) {
      formData = config.data
    }

    const result = await nativeQueueManager.enqueueRequest(config, formData)

    // Immediately trigger sync if online (non-blocking)
    checkNetworkConnected()
      .then(isConnected => {
        if (isConnected) {
          debug('Online, triggering immediate sync')
          nativeQueueManager.performSync().catch(err => {
            debug('Sync failed, will retry later:', err)
          })
        }
      })
      .catch(err => {
        debug('Network check failed:', err)
      })

    const mockResponse = {
      data: { status: result.status, metadata: result.metadata } as {
        status: string
        metadata: unknown
      },
      status: 202,
      statusText: 'Accepted',
      headers: {},
      config,
    }

    // Dev-harness observation: record the mock 202 in the bus and wrap
    // `data` in the Proxy that flags consumer reads of keys outside
    // `{status, metadata}`. The whole block + its imports + the bus
    // module are tree-shaken from production builds. `verify-prod-build`
    // asserts the bus's domain marker is absent from `dist/`.
    if (__DEV_HARNESS__) {
      const requestUUID =
        (result.metadata as { requestUUID?: string } | null | undefined)?.requestUUID ?? null
      const entry = emitMock202({
        ts: Date.now(),
        method: method as 'POST' | 'PATCH',
        url,
        requestUUID,
        status: result.status,
        metadata: result.metadata,
      })
      mockResponse.data = wrapMock202Data(mockResponse.data, entry)
    }

    return Promise.reject(new NativeQueuedResponse(mockResponse, config))
  }

  return config
}

// Track request start times for slow connection detection (WeakMap avoids config mutation)
const requestStartTimes = new WeakMap<InternalAxiosRequestConfig, number>()

// Probes whose result has already been recorded. Concurrent slow samples share
// one probe (`measureLinkLatency` dedupes), and that probe is ONE measurement:
// recording it once per waiting request would let a single transient reading
// fill the detector's window on its own.
const recordedProbes = new WeakSet<Promise<number | null>>()

/**
 * Wraps the adapter of a request that opted out of latency sampling so it is
 * counted in `useServerWorkStore` exactly while it is on the wire.
 *
 * Counted at the ADAPTER, not in the interceptors: axios runs `transformRequest`
 * between the request interceptor and the adapter, and a throw there (a payload
 * that cannot be serialized) reaches the error interceptor as a plain Error with
 * no config — an interceptor-paired begin/end would leak and hold the push
 * prompt off for the rest of the session. `finally` here cannot be skipped.
 */
const countAsServerWork = (config: InternalAxiosRequestConfig): void => {
  const delegate = axios.getAdapter(config.adapter ?? api.defaults.adapter)
  const counted: AxiosAdapter = async adapterConfig => {
    useServerWorkStore.getState().begin()
    try {
      return await delegate(adapterConfig)
    } finally {
      useServerWorkStore.getState().end()
    }
  }
  config.adapter = counted
}

/**
 * Single exit point for the slow-connection latency sample: consume the start
 * time stamped by the request interceptor and feed the elapsed time to the
 * detector — unless this particular request opted out.
 *
 * Both interceptors route through here so the opt-out cannot be honoured on
 * one path and forgotten on the other; a request that errors or times out is
 * not sampled either if it was opted out.
 *
 * `isRoundTrip` is what distinguishes the two callers: a response (any status)
 * and a timeout are real round-trips, a bare ERR_NETWORK is not.
 *
 * `skipLatencySampling` is declared on `AxiosRequestConfig` in
 * `lib/slowConnectionDetector.ts`, next to the thresholds it protects.
 * The start time is dropped either way — an opted-out request must not leave
 * a stale stamp behind.
 *
 * A sample above `SLOW_THRESHOLD_MS` is NOT recorded as-is. Its duration is link
 * time plus server time, and on its own it cannot say which one was slow — on
 * 2026-10-16 a 44 s report render held the PHP session lock, the user's other
 * requests timed out at 15 s behind it, and the banner blamed a healthy network.
 * Such a sample is replaced by a probe of the link alone (`linkLatencyProbe.ts`):
 * a slow link makes the probe slow too and the detector still fires; a slow
 * server leaves the probe fast and the detector is told the truth. Fast samples
 * skip the probe — they already prove the link is fine.
 */
const settleLatencySample = (config: InternalAxiosRequestConfig, isRoundTrip: boolean): void => {
  const startTime = requestStartTimes.get(config)
  if (startTime === undefined) return
  requestStartTimes.delete(config)
  if (!isRoundTrip || config.skipLatencySampling) return

  const elapsed = Date.now() - startTime
  if (elapsed <= SLOW_THRESHOLD_MS) {
    slowConnectionDetector.recordLatency(elapsed)
    return
  }
  const probe = measureLinkLatency()
  if (recordedProbes.has(probe)) return
  recordedProbes.add(probe)
  void probe.then(linkMs => {
    if (linkMs !== null) slowConnectionDetector.recordLatency(linkMs)
  })
}

api.interceptors.request.use(async config => {
  requestStartTimes.set(config, Date.now())

  // Every request says which client sent it (AUTHZ-02 E2, decision 3) — set before queue routing,
  // so the stored entry carries them too; the replay re-stamps them with the replaying build's
  // identity (`apiFetch`).
  for (const [name, value] of Object.entries(await clientIdentityHeaders())) {
    config.headers[name] = value
  }

  if (typeof window !== 'undefined') {
    config.headers['User-Locale'] = localStorage.getItem(LOCAL_STORAGE_LANG_KEY) || 'pl'

    const method = config.method?.toUpperCase()
    const url = config.url || ''
    const isQueueable = isQueueableUrl(url)
    const useQueue = shouldQueueMutation()

    debug(`Request: ${method} ${url}, isQueueable=${isQueueable}, useQueue=${useQueue}`)

    if (useQueue && (method === 'POST' || method === 'PATCH') && isQueueable) {
      debug('>>> Routing to native queue')
      return handleNativeQueueRequest(config)
    }

    // A mandatory web update is pending (useWebUpdateStore): start no new server work. Queued
    // writes above still enqueue and drain, so a draft can be finished; everything else is
    // refused the way an offline request is — ERR_NETWORK, which no caller toasts — until the
    // reload replaces this page.
    if (isWebUpdateRequired()) {
      throw new AxiosError('Update required', 'ERR_NETWORK', config)
    }

    // Dev-harness offline override. Queueable mutations have already
    // routed to the native queue above (they don't depend on network);
    // everything else — GETs, DELETEs, non-queueable POST/PATCH — would
    // otherwise hit the network adapter, and that's the surface this
    // override simulates as "no network."
    //
    // The whole block is dead code when `__DEV_HARNESS__` is false
    // (Vite `define` resolves it to the literal `false` at compile
    // time), so the symbol use + the static import at the top of this
    // file + the entire networkTransport / networkStore module graph
    // are tree-shaken from production builds. `verify-prod-build`
    // asserts no `getHarnessAxiosOverride` reference reaches `dist/`.
    if (__DEV_HARNESS__) {
      if (getHarnessAxiosOverride() === 'offline') {
        throw new AxiosError('Network Error (harness offline)', 'ERR_NETWORK', config)
      }
    }
  }

  // Counted only once the request is really going to the network: the queue
  // and harness branches above never reach the server, and a request that
  // never starts must not hold the count up.
  if (config.skipLatencySampling) countAsServerWork(config)
  return config
})

api.interceptors.response.use(
  response => {
    settleLatencySample(response.config, true)

    // Every caller of `api` reads `contentUrl*`; from E3 on the signed URL rides next to it in
    // `signedContentUrl*` and this is where it takes over (AUTHZ-02 E2). In place, before any
    // caller or cache sees the body; a no-op while the API sends no signed field.
    adoptSignedMediaUrls(response.data)

    // Auto-invalidate React Query caches after successful mutations.
    // Queueable endpoints (/issues, /comments, /media_objects) are updated via
    // Mercure SSE after the Dexie queue syncs — no cache invalidation here.
    // All other endpoints: invalidate normally on successful mutation.
    const method = response.config.method?.toUpperCase()
    if (method && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
      const url = response.config.url ?? ''
      // Only suppress invalidation for methods actually routed through the
      // Dexie queue (POST/PATCH). DELETE goes straight to the server and
      // needs normal cache invalidation — Mercure alone is not sufficient
      // because the delete response arrives before the SSE event.
      const isQueuedMethod = method === 'POST' || method === 'PATCH'
      const isQueuedEndpoint = isQueuedMethod && isQueueableUrl(url)
      const shouldSkip = MUTATION_INVALIDATION_SKIP.some(skip => url.includes(skip))

      // Non-queued method (e.g. DELETE) landing on a queueable resource
      // while writes for that resource are still in flight: defer
      // invalidation. Refetching now races the draining queue and
      // overwrites optimistic state. `useQueueDrainInvalidator` will fire
      // a single invalidation once the queue is empty.
      const queuedType = QUEUED_ENDPOINT_TYPES.find(([seg]) => url.includes(seg))?.[1]
      const queueIsBusy = !!queuedType && hasPendingQueueEntriesForType(queuedType)

      if (!isQueuedEndpoint && !shouldSkip && !queueIsBusy) {
        for (const [segment, queryKeys] of Object.entries(MUTATION_INVALIDATION_MAP)) {
          if (url.includes(segment)) {
            for (const key of queryKeys) {
              queryClient.invalidateQueries({ queryKey: [key] })
            }
          }
        }
      }
    }

    return response
  },
  async error => {
    // Record latency for errors that reflect actual network round-trips:
    // - Responses (4xx/5xx): server was reached, latency is real
    // - Timeouts (ECONNABORTED): request hung, latency = timeout value
    // Skip ERR_NETWORK (truly offline) — not a latency signal.
    if (error instanceof AxiosError && error.config) {
      settleLatencySample(error.config, Boolean(error.response) || error.code === 'ECONNABORTED')
    }

    if (error instanceof NativeQueuedResponse) {
      debug('Native queue request accepted')
      return error.response
    }

    // Maintenance window: a non-team request was 503'd with the maintenance
    // marker. Flip the app into the overlay now (don't wait up to 30s for the
    // next poll) and stay quiet — no error toast, no /login redirect.
    if (error instanceof AxiosError && isMaintenance503(error)) {
      // keep key in sync with maintenanceStatusQueryKey in useMaintenanceStatus.tsx
      void queryClient.invalidateQueries({ queryKey: ['maintenanceStatus'] })
      return Promise.reject(error)
    }

    // 5xx and timeouts are otherwise invisible to BayaWatch when the UI
    // handles the rejection (see `httpFailureReporting.ts`). Placed after the
    // mock-202 and maintenance-503 exits so neither is ever reported.
    reportHttpFailure(error)

    if (error.response && error.request?.responseURL) {
      const relativePath = window.location.pathname + window.location.search + window.location.hash
      const loginPath = '/login'
      if (window.location.pathname === loginPath || error.request.responseURL.includes('/login')) {
        return Promise.reject(error)
      }

      if (error.response.status === 401) {
        const requestUrl = error.config?.url || error.request.responseURL
        if (shouldRedirectUnauthorizedToLogin(window.location.pathname, requestUrl)) {
          console.error(`redirecting to ${loginPath} for ${relativePath} due to 401`)
          window.location.href = loginPath
        }
        return Promise.reject(error)
      }

      // 403 on the auth endpoint means session expired (not a permission error on a resource)
      if (error.response.status === 403) {
        const requestUrl = error.config?.url || ''
        if (requestUrl.includes('/users/me')) {
          console.error(
            `redirecting to ${loginPath} for ${relativePath} due to 403 on auth endpoint`,
          )
          window.location.href = loginPath
          return Promise.reject(error)
        }
      }
    }
    return Promise.reject(error)
  },
)

export { api }

// React Query
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: Error, query) => {
      debug('Query error:', error)
      if ((query.options as { throwOnError?: boolean }).throwOnError === false) return
      if (
        error instanceof AxiosError &&
        !isOfflineOrTimeoutError(error) &&
        !isMaintenance503(error)
      ) {
        toast({
          variant: 'destructive',
          title: i18n.t('common.error'),
          description: error?.response?.data?.detail,
        })
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: Error) => {
      debug('Mutation error:', error)
      if (
        error instanceof AxiosError &&
        !isOfflineOrTimeoutError(error) &&
        !isMaintenance503(error)
      ) {
        // TODO(server-409): Server currently uses last-write-wins and never returns 409.
        // This handler is forward-looking plumbing — it will activate when the server
        // adds updatedAt/ETag conflict checking on PATCH endpoints.
        // NOTE: MutationCache.onError fires BEFORE per-mutation onError callbacks.
        // The `return` below prevents the generic toast but does NOT prevent
        // mutation-level error handlers from also firing. If individual mutation hooks
        // add their own error handling, they should also guard against 409.
        // A licence refusal (403 + X-Licence-Block) explains itself instead of the generic
        // error. Queued writes never reach this: they resolve with a mock 202 (see C8).
        if (toastLicenceBlock(error)) return
        if (error.response?.status === 409) {
          toast({
            variant: 'destructive',
            title: i18n.t('conflict.title'),
            description: i18n.t('conflict.description'),
          })
          const requestUrl = error.config?.url || ''
          if (requestUrl.includes('/issues')) {
            queryClient.invalidateQueries({ queryKey: ['singleIssue'] })
            queryClient.invalidateQueries({ queryKey: ['stageIssuesByIssueType'] })
          }
          if (requestUrl.includes('/comments')) {
            queryClient.invalidateQueries({ queryKey: ['singleIssueComments'] })
          }
          return
        }
        toast({
          variant: 'destructive',
          title: i18n.t('common.error'),
          description: error?.response?.data?.detail,
        })
      }
    },
  }),
  defaultOptions: {
    mutations: {
      networkMode: 'always',
    },
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 3,
      // gcTime must be >= maxAge in persistOptions for offline support
      // This keeps cached data available for 7 days for offline access
      gcTime: SEVEN_DAYS,
      refetchOnReconnect: 'always',
      // Don't throw errors to error boundaries - handle via isError state instead
      // This prevents offline/network errors from crashing the app
      throwOnError: false,
      retry: false,
    },
  },
})
